import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_recordEvent } from '@/services/hooks';
import type { RecordFishGroupEventRequest } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';
import type { FishGroupManualEventType } from './types';

/**
 * What the UI may record, expressed in domain terms.
 *
 * `eventType` is widened away from the generated DTO's union on purpose: the
 * generated `services/` types are regenerated from a running backend, so they
 * still predate `delousing`. The domain owns the list of types the UI offers
 * (`FISH_GROUP_MANUAL_EVENT_TYPES`), and the single cast below is the one place
 * that bridges the two — which is also where `@/services` imports belong.
 * Once `yarn updateApi` runs against a backend carrying the delousing enum
 * value, the cast can go and this type can collapse back to the generated one.
 */
export interface RecordFishGroupEventInput extends Omit<RecordFishGroupEventRequest, 'eventType'> {
    eventType: FishGroupManualEventType;
}

export interface UseRecordFishGroupEventResult extends MutationState {
    recordEvent: (requestBody: RecordFishGroupEventInput) => Promise<void>;
}

interface UseRecordFishGroupEventOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export const useRecordFishGroupEvent = (
    clientId: number,
    fishGroupId: number,
    options?: UseRecordFishGroupEventOptions
): UseRecordFishGroupEventResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_recordEvent();

    const recordEvent = async (requestBody: RecordFishGroupEventInput): Promise<void> => {
        try {
            await mutation.mutateAsync({
                clientId,
                fishGroupId,
                requestBody: requestBody as RecordFishGroupEventRequest,
            });
            await invalidateFishGroupQueries(queryClient, clientId, fishGroupId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        recordEvent,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
